import React, { useState, useEffect, useRef } from "react";
import { useToast } from "../../hooks/api/use-toast";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { MediumText } from "../../styles/Typography";
import {
  StyledPickListWrapper,
  Column,
  ButtonContainer,
} from "./LicenseMembersPickList.styles";

interface LicenseMembersPickListProps {
  licenseId: string;
  currentMembers: any[];
  allUsers: { id: string; fullName: string }[];
  maxMembers: number;
  onHide: () => void;
  handlePostSessionMember: (
    licenseId: string,
    userId: string
  ) => Promise<boolean>;
  handleDeleteSessionMember: (
    licenseId: string,
    userId: string
  ) => Promise<boolean>;
  refetchData: () => void;
}

const LicenseMembersPickList: React.FC<LicenseMembersPickListProps> = ({
  licenseId,
  currentMembers,
  allUsers,
  maxMembers,
  onHide,
  handlePostSessionMember,
  handleDeleteSessionMember,
  refetchData,
}) => {
  const { toastRef } = useToast();
  const [source, setSource] = useState<any>([]);
  const [target, setTarget] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const previousTargetLength = useRef<number>(0);

  useEffect(() => {
    if (!allUsers || !currentMembers) return;

    const targetMembers = currentMembers.map((item: any) => {
      const profile = item.profile || item;
      const id = profile?.userId ?? profile?.id;
      const fullName =
        profile?.fullName ||
        [profile?.lastName, profile?.name, profile?.patronymic]
          .filter(Boolean)
          .join(" ")
          .trim();

      return {
        id: String(id),
        fullName: fullName || "Без имени",
      };
    });

    const targetIdSet = new Set(targetMembers.map((m) => m.id));

    const sourceMembers = allUsers
      .filter((u) => !targetIdSet.has(String(u.id)))
      .map((u) => ({
        id: String(u.id),
        fullName: u.fullName,
      }));

    setSource(sourceMembers);
    setTarget(targetMembers);
  }, [allUsers, currentMembers]);

  const handleChange = (e: any) => {
    const newTarget = e.target;
    setSource(e.source);
    setTarget(newTarget);
    previousTargetLength.current = newTarget.length;
  };

  const handleSave = async () => {
    setLoading(true);
    const targetIds = target.map((t: any) => t.id);
    const originalIds = currentMembers.map((item: any) => {
      const profile = item.profile || item;
      return String(profile?.userId ?? profile?.id);
    });
    const toAdd = targetIds.filter((id: string) => !originalIds.includes(id));
    const toRemove = originalIds.filter((id) => !targetIds.includes(id));

    try {
      for (const id of toAdd) {
        const ok = await handlePostSessionMember(licenseId, id);
        if (!ok) return;
      }
      for (const id of toRemove) {
        const ok = await handleDeleteSessionMember(licenseId, id);
        if (!ok) return;
      }
      refetchData();
      onHide();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const itemTemplate = (item: any) => <span>{item?.fullName}</span>;

  return (
    <Column>
      <Toast ref={toastRef} position="bottom-center" />
      <MediumText>
        Выбрано участников: {target.length} из {maxMembers}
      </MediumText>
      <StyledPickListWrapper
        dataKey="id"
        source={source}
        target={target}
        onChange={handleChange}
        sourceHeader="Доступные пользователи"
        targetHeader="Участники сессии"
        itemTemplate={itemTemplate}
        filter
        filterBy="fullName"
        sourceFilterPlaceholder="Поиск"
        targetFilterPlaceholder="Поиск"
        sourceStyle={{
          height: "20rem",
        }}
        targetStyle={{
          height: "20rem",
        }}
        breakpoint="768px"
        showSourceControls={false}
        showTargetControls={false}
      />

      <ButtonContainer>
        <Button label="Отмена" onClick={onHide} outlined />
        <Button label="Сохранить" onClick={handleSave} loading={loading} />
      </ButtonContainer>
    </Column>
  );
};

export default LicenseMembersPickList;
